import {
  createContext,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
  useContext,
  type JSX,
  type ParentProps,
} from "solid-js";
import { exporterCsv, exporterPng, exporterSvg, nomFichier, type Tableau } from "../export";
import { Icone } from "./Icone";

// Ce que la vue affichée sait exporter. Chaque vue le déclare en se dessinant :
// l'export ne devine pas ce qu'il y a à l'écran (POC, app.js `figure()`).

export interface FigureExportable {
  /** Identifiant, sert au nom des fichiers exportés. */
  id: string;
  /** Le dessin, s'il y en a un. */
  svg?: () => SVGSVGElement | undefined;
  tableau?: () => Tableau;
}

interface Registre {
  declarer(f: FigureExportable): void;
  retirer(f: FigureExportable): void;
}

const ContexteFigures = createContext<Registre>();

/** Tient la liste des figures de la vue courante. */
export function creerRegistreFigures() {
  const [figures, setFigures] = createSignal<FigureExportable[]>([]);
  const registre: Registre = {
    declarer: (f) => setFigures((l) => [...l, f]),
    retirer: (f) => setFigures((l) => l.filter((x) => x !== f)),
  };
  const Fournisseur = (props: ParentProps) => (
    <ContexteFigures.Provider value={registre}>{props.children}</ContexteFigures.Provider>
  );
  return { figures, Fournisseur };
}

/** Déclare une figure exportable le temps que le composant appelant est monté. */
export function declarerFigure(f: FigureExportable): void {
  const registre = useContext(ContexteFigures);
  if (!registre) return;
  onMount(() => registre.declarer(f));
  onCleanup(() => registre.retirer(f));
}

/** En-tête d'une figure : titre et lecture en une phrase, puis le dessin. */
export function Figure(
  props: FigureExportable & {
    titre: string;
    /** Comment lire la figure, en une phrase. */
    lecture?: JSX.Element;
    children: JSX.Element;
  },
) {
  // eslint-disable-next-line solid/reactivity -- les accesseurs sont lus à l'export, pas au rendu
  declarerFigure({ id: props.id, svg: props.svg, tableau: props.tableau });
  return (
    <section class="figure" aria-labelledby={`figure-${props.id}`}>
      <div class="entete-vue">
        <h2 id={`figure-${props.id}`}>{props.titre}</h2>
        <Show when={props.lecture}>
          <p class="explication discret">{props.lecture}</p>
        </Show>
      </div>
      {props.children}
    </section>
  );
}

/**
 * Menu d'export : replié, parce que trois commandes toujours visibles prenaient
 * beaucoup de place pour un geste occasionnel. Il agit sur la vue affichée et
 * fait choisir la figure quand elle en contient plusieurs.
 */
export function MenuExport(props: {
  figures: FigureExportable[];
  surErreur: (message: string) => void;
}) {
  const [ouvert, setOuvert] = createSignal(false);
  const [choix, setChoix] = createSignal(0);
  const courante = () => props.figures[choix()] ?? props.figures[0];
  let enveloppe: HTMLDivElement | undefined;

  onMount(() => {
    // Un menu qui ne se ferme pas au clic à côté finit par masquer la vue.
    const fermerDehors = (e: MouseEvent) => {
      if (enveloppe && !enveloppe.contains(e.target as Node)) setOuvert(false);
    };
    const fermerEchap = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOuvert(false);
    };
    document.addEventListener("click", fermerDehors);
    document.addEventListener("keydown", fermerEchap);
    onCleanup(() => {
      document.removeEventListener("click", fermerDehors);
      document.removeEventListener("keydown", fermerEchap);
    });
  });

  const lancer = (format: "png" | "svg" | "csv") => {
    setOuvert(false);
    const f = courante();
    if (!f) return;
    const nom = nomFichier(f.id);
    if (format === "csv") {
      if (!f.tableau) return props.surErreur("Cette figure n'a pas de tableau équivalent.");
      return exporterCsv(f.tableau(), nom);
    }
    const svg = f.svg?.();
    if (!svg) {
      return props.surErreur("Cette figure n'est pas un dessin : seul l'export CSV s'y applique.");
    }
    if (format === "svg") exporterSvg(svg, nom);
    else exporterPng(svg, nom, props.surErreur);
  };

  const ENTREES = [
    { format: "png", titre: "Image PNG", detail: "pour une diapositive" },
    { format: "svg", titre: "Image SVG", detail: "nette à toute taille" },
    { format: "csv", titre: "Tableau CSV", detail: "les chiffres du dessin" },
  ] as const;

  return (
    <div class="menu" ref={(e) => (enveloppe = e)}>
      <button
        type="button"
        class="bouton bouton-outil bouton-menu"
        aria-haspopup="true"
        aria-expanded={ouvert()}
        onClick={() => setOuvert(!ouvert())}
      >
        <Icone nom="telecharger" />
        Exporter <span class="chevron">▾</span>
      </button>
      <Show when={ouvert()}>
        <div class="menu-panneau">
          <Show when={props.figures.length > 1}>
            <select
              aria-label="Figure à exporter"
              value={String(Math.min(choix(), props.figures.length - 1))}
              onChange={(e) => setChoix(Number(e.currentTarget.value))}
            >
              <For each={props.figures}>
                {(f, i) => <option value={String(i())}>{f.id.replace(/-/g, " ")}</option>}
              </For>
            </select>
          </Show>
          <For each={ENTREES}>
            {(e) => (
              <button type="button" class="menu-entree" onClick={() => lancer(e.format)}>
                <span class="menu-titre">{e.titre}</span>
                <span class="menu-detail">{e.detail}</span>
              </button>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
